import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  CheckCircle2, 
  Clock, 
  FileText, 
  Download, 
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { getStatusBadge as getStatusConfig } from '../constants/statuses';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkflowPanel = ({ operation, onStatusChange }) => {
  const { user } = useAuth();
  const [statusHistory, setStatusHistory] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Statuts visibles côté CLIENT (vue simplifiée)
  const CLIENT_VISIBLE_STATUSES = [
    'demande_enregistree',
    'en_attente_de_devis',
    'en_attente_validation_client',
    'en_negociation',
    'planifiee',
    'annulee_par_client',
    'en_cours_de_chargement',
    'charge_en_route',
    'livree',
    'en_attente_paiement',
    'payee',
    'terminee',
    'annulee_admin'
  ];

  // Fonction pour créer le badge de statut
  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <Badge className={`${config.bgColor} ${config.textColor} border-0`}>
        {config.icon} {config.shortLabel}
      </Badge>
    );
  };

  // Filtrer l'historique selon le rôle
  const getFilteredHistory = () => {
    if (user?.role === 'client') {
      // Client ne voit que les statuts de sa vue simplifiée
      return statusHistory.filter(entry => 
        CLIENT_VISIBLE_STATUSES.includes(entry.new_status)
      );
    }
    // Admin voit tout
    return statusHistory;
  };

  useEffect(() => {
    if (operation?.id) {
      fetchStatusHistory();
      fetchAvailableActions();
    }
  }, [operation?.id]);

  const fetchStatusHistory = async () => {
    try {
      const response = await axios.get(`${API}/operation-status/${operation.id}/history`);
      setStatusHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching status history:', error);
    }
  };

  const fetchAvailableActions = async () => {
    try {
      const response = await axios.get(`${API}/operation-status/${operation.id}/available-actions`);
      setAvailableActions(response.data.actions || []);
    } catch (error) {
      console.error('Error fetching available actions:', error);
    }
  };

  const handleStatusChange = async (newStatus, reason = null) => {
    setLoading(true);
    try {
      await axios.post(`${API}/operation-status/${operation.id}/change`, {
        new_status: newStatus,
        reason: reason,
        validate_requirements: false  // Les modals feront la validation
      });

      toast({
        title: "✅ Statut mis à jour",
        description: `L'opération est maintenant : ${newStatus}`
      });

      // Rafraîchir les données
      await fetchStatusHistory();
      await fetchAvailableActions();
      
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (error) {
      console.error('Error changing status:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de changer le statut",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      demande_enregistree: 'bg-blue-50 border-blue-200',
      en_attente_de_devis: 'bg-yellow-50 border-yellow-200',
      en_attente_validation_client: 'bg-purple-50 border-purple-200',
      en_negociation: 'bg-pink-50 border-pink-200',
      devis_accepte: 'bg-teal-50 border-teal-200',
      planifiee: 'bg-indigo-50 border-indigo-200',
      prete_au_chargement: 'bg-indigo-50 border-indigo-200',
      en_cours_de_chargement: 'bg-orange-50 border-orange-200',
      charge_en_route: 'bg-cyan-50 border-cyan-200',
      livree: 'bg-teal-50 border-teal-200',
      en_attente_paiement: 'bg-amber-50 border-amber-200',
      payee: 'bg-green-50 border-green-200',
      terminee: 'bg-gray-50 border-gray-200',
      annulee_admin: 'bg-red-50 border-red-200',
      annulee_par_client: 'bg-red-50 border-red-200'
    };
    return colors[status] || 'bg-gray-50 border-gray-200';
  };

  // Boutons d'action pour le client
  const renderClientActions = () => {
    if (user?.role !== 'client') return null;
    
    const currentStatus = operation.status;

    // EN_ATTENTE_VALIDATION_CLIENT : Accepter, Refuser, Négocier
    if (currentStatus === 'en_attente_validation_client') {
      return (
        <div className="space-y-3">
          <Alert className="bg-orange-50 border-orange-200">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              <strong>Action requise :</strong> Veuillez accepter ou refuser le devis, ou proposer un autre tarif.
            </AlertDescription>
          </Alert>
          
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => handleStatusChange('planifiee')}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 w-full"
            >
              <ThumbsUp className="w-4 h-4 mr-2" />
              Accepter le devis
            </Button>
            
            <Button
              onClick={() => handleStatusChange('en_negociation')}
              disabled={loading}
              variant="outline"
              className="w-full"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Proposer un autre tarif
            </Button>
            
            <Button
              onClick={() => handleStatusChange('annulee_par_client')}
              disabled={loading}
              variant="outline"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Refuser le devis
            </Button>
          </div>
        </div>
      );
    }

    // EN_NEGOCIATION : Message informatif
    if (currentStatus === 'en_negociation') {
      return (
        <Alert className="bg-blue-50 border-blue-200">
          <Clock className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Votre proposition tarifaire est en cours d'étude par notre équipe commerciale.
          </AlertDescription>
        </Alert>
      );
    }

    // EN_ATTENTE_DE_DEVIS : Attente action équipe Baobab
    if (currentStatus === 'en_attente_de_devis' || currentStatus === 'demande_enregistree') {
      return (
        <Alert className="bg-blue-50 border-blue-200">
          <Clock className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Votre demande est en cours de traitement.</strong>
            <br />
            Notre équipe prépare votre devis. Vous serez notifié dès qu'il sera disponible pour validation.
          </AlertDescription>
        </Alert>
      );
    }

    // Statuts en cours : Lecture seule
    if (['planifiee', 'en_cours_de_chargement', 'charge_en_route', 'livree'].includes(currentStatus)) {
      return (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            Votre opération est en cours. Consultez les informations logistiques ci-dessous.
          </AlertDescription>
        </Alert>
      );
    }

    // Statuts terminaux : Message de clôture
    if (['terminee', 'annulee_admin', 'annulee_par_client'].includes(currentStatus)) {
      return (
        <Alert>
          <AlertDescription>
            Cette opération est clôturée. Aucune action supplémentaire n'est possible.
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#1db584]" />
          Workflow & Devis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Statut actuel */}
        {(user?.role !== 'client' || CLIENT_VISIBLE_STATUSES.includes(operation.status)) && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Statut Actuel</h3>
            <div className="flex items-center gap-3">
              {getStatusBadge(operation.status)}
              <span className="text-sm text-gray-500">
                Mis à jour le {formatDateTime(operation.updatedAt)}
              </span>
            </div>
          </div>
        )}
        
        {/* Message si statut interne pour le client */}
        {user?.role === 'client' && !CLIENT_VISIBLE_STATUSES.includes(operation.status) && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              Votre opération est en cours de traitement par notre équipe.
            </AlertDescription>
          </Alert>
        )}

        {/* Actions client */}
        {renderClientActions()}

        {/* Détail du devis */}
        {operation.estimatedPrice && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Détail du Devis</h3>
            
            {/* Quote Items Table */}
            {operation.quoteItems && operation.quoteItems.length > 0 && (
              <div className="mb-4 overflow-x-auto">
                <table className="w-full text-sm border rounded-lg">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2 border-b font-semibold">Détail trajet</th>
                      <th className="text-center p-2 border-b font-semibold">Poids (T)</th>
                      <th className="text-left p-2 border-b font-semibold">Véhicule</th>
                      <th className="text-center p-2 border-b font-semibold">Qté</th>
                      <th className="text-right p-2 border-b font-semibold">P.U. HT</th>
                      <th className="text-right p-2 border-b font-semibold">Total HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operation.quoteItems.map((item, index) => (
                      <tr key={index} className="border-b last:border-0">
                        <td className="p-2 whitespace-pre-line">{item.designation}</td>
                        <td className="p-2 text-center">{item.poids || '-'}</td>
                        <td className="p-2">{item.unite_payante}</td>
                        <td className="p-2 text-center">{item.quantite}</td>
                        <td className="p-2 text-right">{item.prix_unitaire_ht.toLocaleString('fr-FR')}</td>
                        <td className="p-2 text-right font-semibold">{item.montant_total_ht.toLocaleString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Montant Total HT</span>
                <span className="font-semibold">
                  {(operation.montantTotalHT || operation.estimatedPrice / 1.18).toFixed(0).toLocaleString('fr-FR')} XOF
                </span>
              </div>
              {operation.insuranceAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Assurance ({operation.insuranceRate}%)</span>
                  <span className="font-semibold">
                    {operation.insuranceAmount.toLocaleString('fr-FR')} XOF
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">TVA ({operation.tauxTVA || 18}%)</span>
                <span className="font-semibold">
                  {(operation.montantTVA || (operation.estimatedPrice - operation.estimatedPrice / 1.18)).toFixed(0).toLocaleString('fr-FR')} XOF
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-base font-bold text-gray-900">Montant Total TTC</span>
                <span className="text-lg font-bold text-[#1db584]">
                  {(operation.montantTotalTTC || operation.estimatedPrice).toLocaleString('fr-FR')} XOF
                </span>
              </div>
            </div>

            {operation.quoteNotes && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs font-semibold text-blue-900 mb-1">Notes :</p>
                <p className="text-xs text-blue-700">{operation.quoteNotes}</p>
              </div>
            )}
          </div>
        )}

        {/* Timeline / Historique */}
        {getFilteredHistory().length > 0 && (
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Historique des Statuts
              {user?.role === 'client' && (
                <span className="text-xs text-gray-500 ml-2 font-normal">(vue simplifiée)</span>
              )}
            </h3>
            <div className="space-y-3">
              {getFilteredHistory().map((entry, index) => {
                const isCurrentStatus = entry.new_status === operation.status;
                return (
                  <div
                    key={entry.id}
                    className={`relative pl-6 pb-3 ${
                      index !== statusHistory.length - 1 ? 'border-l-2 border-gray-200' : ''
                    }`}
                  >
                    <div className={`absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px] ${
                      isCurrentStatus ? 'bg-[#1db584] ring-4 ring-[#1db584]/20' : 'bg-gray-300'
                    }`} />
                  
                  <div className={`rounded-lg border p-3 ${getStatusColor(entry.new_status)} ${
                    isCurrentStatus ? 'ring-2 ring-[#1db584] shadow-md' : ''
                  }`}>
                    <div className="flex items-start justify-between mb-1">
                      <span className={`text-sm font-semibold capitalize ${
                        isCurrentStatus ? 'text-[#1db584]' : ''
                      }`}>
                        {entry.new_status.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDateTime(entry.changed_at)}
                      </span>
                    </div>
                    {user?.role !== 'client' ? (
                      // Vue admin : afficher tous les détails
                      <>
                        <p className="text-xs text-gray-600">
                          Par {entry.changed_by_name} ({entry.changed_by_role})
                        </p>
                        {entry.reason && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            Raison : {entry.reason}
                          </p>
                        )}
                      </>
                    ) : (
                      // Vue client : simplifiée
                      <p className="text-xs text-gray-600">
                        Mis à jour par l'équipe Baobab Connect
                      </p>
                    )}
                  </div>
                </div>
              )}
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkflowPanel;
